import { useState, useEffect } from "react";
import { ArrowLeft, Calendar, CreditCard, FileText, Crown, Zap, MessageCircle, Image, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Subscription {
  id: string;
  plan_name: string;
  plan_price: number;
  status: string;
  started_at: string;
  expires_at: string | null;
}

interface Invoice {
  id: string;
  invoice_number: string;
  amount: number;
  status: string;
  issued_at: string;
  paid_at: string | null;
}

interface SubscriptionHistoryProps {
  isOpen: boolean;
  onClose: () => void;
}

const getPlanIcon = (planName: string) => {
  switch (planName.toLowerCase()) {
    case 'ultimate':
      return <Crown className="w-5 h-5" />;
    case 'pro':
      return <Image className="w-5 h-5" />;
    case 'plus':
      return <Zap className="w-5 h-5" />;
    default:
      return <MessageCircle className="w-5 h-5" />;
  }
};

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'active':
    case 'paid':
      return 'bg-green-500/10 text-green-500';
    case 'expired':
    case 'cancelled':
      return 'bg-red-500/10 text-red-500';
    case 'pending':
      return 'bg-yellow-500/10 text-yellow-500';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const SubscriptionHistory = ({ isOpen, onClose }: SubscriptionHistoryProps) => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const [subsResponse, invResponse] = await Promise.all([
        supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false }),
        supabase
          .from('invoices')
          .select('*')
          .eq('user_id', user.id)
          .order('issued_at', { ascending: false })
      ]);

      if (subsResponse.data) setSubscriptions(subsResponse.data);
      if (invResponse.data) setInvoices(invResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent className="h-[90vh] bg-background/95 backdrop-blur-xl border-t border-border/50">
        <DrawerHeader className="relative border-b border-border/30 pb-4">
          <button
            onClick={() => onClose()}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted/50 transition-all duration-300 hover:scale-110 active:scale-90"
          >
            <ArrowLeft className="w-5 h-5 text-foreground" />
          </button>
          <DrawerTitle className="text-xl font-bold text-center">Storico Abbonamenti</DrawerTitle>
        </DrawerHeader>

        <div className="flex flex-col p-4 overflow-hidden h-full">
          <Tabs defaultValue="subscriptions" className="flex-1 flex flex-col">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="subscriptions" className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Abbonamenti
              </TabsTrigger>
              <TabsTrigger value="invoices" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Fatture
              </TabsTrigger>
            </TabsList>

            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <TabsContent value="subscriptions" className="flex-1 overflow-y-auto">
                  {subscriptions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <CreditCard className="w-12 h-12 text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Nessun abbonamento attivo</p>
                      <p className="text-sm text-muted-foreground/70 mt-1">
                        I tuoi abbonamenti appariranno qui
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {subscriptions.map((sub) => (
                        <div
                          key={sub.id}
                          className="p-4 rounded-xl bg-card/80 border border-border/50 backdrop-blur-sm"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
                                {getPlanIcon(sub.plan_name)}
                              </div>
                              <div>
                                <p className="font-semibold text-foreground">{sub.plan_name}</p>
                                <p className="text-sm text-muted-foreground">
                                  €{sub.plan_price.toFixed(2)}/mese
                                </p>
                              </div>
                            </div>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(sub.status)}`}>
                              {sub.status === 'active' ? 'Attivo' : sub.status}
                            </span>
                          </div>
                          <div className="mt-3 pt-3 border-t border-border/30 flex items-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-4 h-4" />
                              <span>Inizio: {formatDate(sub.started_at)}</span>
                            </div>
                            {sub.expires_at && (
                              <div className="flex items-center gap-1.5">
                                <span>Scadenza: {formatDate(sub.expires_at)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="invoices" className="flex-1 overflow-y-auto">
                  {invoices.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <FileText className="w-12 h-12 text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Nessuna fattura disponibile</p>
                      <p className="text-sm text-muted-foreground/70 mt-1">
                        Le tue fatture appariranno qui
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {invoices.map((inv) => (
                        <div
                          key={inv.id}
                          className="p-4 rounded-xl bg-card/80 border border-border/50 backdrop-blur-sm"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-semibold text-foreground">
                                Fattura #{inv.invoice_number}
                              </p>
                              <p className="text-sm text-muted-foreground mt-1">
                                Emessa il {formatDate(inv.issued_at)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg text-foreground">
                                €{inv.amount.toFixed(2)}
                              </p>
                              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(inv.status)}`}>
                                {inv.status === 'paid' ? 'Pagata' : inv.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </>
            )}
          </Tabs>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default SubscriptionHistory;